import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPercent, faFileExcel, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import DataService from '../../services/DataService';
import TimeStamp from '../../utils/TimeStamp';

const BillsToDeclare = () => {
    const [paidBills, setPaidBills] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Fonction pour formater la date
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        return new TimeStamp(
            timestamp.year,
            timestamp.month,
            timestamp.day,
            timestamp.hour,
            timestamp.minute
        ).toFormattedDate();
    };

    const fetchPaidBills = useCallback(async () => {
        try {
            const bills = await DataService.filter('bills', { status: 'Paid' });

            const billsWithDetails = await Promise.all(bills.map(async (bill) => {
                const quote = await DataService.getById('quotes', bill.quoteId);
                if (!quote) return null;

                const opportunity = await DataService.getById('opportunities', quote.opportunityId);
                if (!opportunity) return null;

                const account = await DataService.getById('accounts', opportunity.accountId);
                if (!account) return null;

                const vat20Setting = await DataService.getByField('settings', 'name', 'TVA20');
                if(!vat20Setting) return null;

                // S'assurer que les montants sont des nombres valides
                const amount = typeof bill.currentBillAmount === 'number' ? bill.currentBillAmount : 0;
                const vat20 = vat20Setting ? parseFloat(vat20Setting.data) : 0;
                const vat = amount * vat20 / 100;

                // Conversion correcte de la date pour la comparaison
                return {
                    accountName: account.name,
                    opportunityName: opportunity.name,
                    billDate: bill.timeStamp,  // Garder l'objet TimeStamp original
                    amount: amount,
                    vat: vat
                };
            }));

            // Filtrer les factures selon la plage de dates
            const filteredBills = billsWithDetails.filter(bill => {
                if (!bill) return false;
                
                // Créer un objet Date à partir du TimeStamp pour la comparaison
                const billDate = new Date(
                    parseInt(bill.billDate.year),
                    parseInt(bill.billDate.month) - 1,
                    parseInt(bill.billDate.day)
                );
                const startDateTime = startDate ? new Date(startDate) : null;
                const endDateTime = endDate ? new Date(endDate) : null;

                if (startDateTime && billDate < startDateTime) return false;
                if (endDateTime && billDate > endDateTime) return false;

                return true;
            });

            setPaidBills(filteredBills);
        } catch (error) {
            console.error('Erreur lors de la récupération des factures payées:', error);
        }
    }, [startDate, endDate]); // Dépendances du useCallback

    useEffect(() => {
        fetchPaidBills();
    }, [startDate, endDate, fetchPaidBills]); // Ajout de fetchPaidBills comme dépendance

    const handleExportExcel = () => {
        try {
            // Préparer les données pour l'export
            const exportData = paidBills.map(bill => ({
                'Compte': bill.accountName,
                'Projet': bill.opportunityName,
                'Date': formatDate(bill.billDate),
                'Montant HT': bill.amount,
                'TVA': bill.vat
            }));

            // Créer un classeur Excel
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(exportData);

            // Formater les colonnes monétaires
            const moneyFormat = '#,##0.00€';
            ['D', 'E'].forEach(col => {
                const range = XLSX.utils.decode_range(ws['!ref']);
                for (let row = range.s.r + 1; row <= range.e.r; row++) {
                    const cell = ws[`${col}${row + 1}`];
                    if (cell) {
                        cell.z = moneyFormat;
                    }
                }
            });

            // Ajuster la largeur des colonnes
            const colWidths = [
                { wch: 30 }, // Compte
                { wch: 30 }, // Projet
                { wch: 12 }, // Date
                { wch: 15 }, // Montant HT
                { wch: 15 }  // TVA
            ];
            ws['!cols'] = colWidths;

            // Ajouter la feuille au classeur
            XLSX.utils.book_append_sheet(wb, ws, 'Factures');

            // Générer le nom du fichier
            const getFormattedDate = (dateStr) => {
                if (!dateStr) return '';
                const date = new Date(dateStr);
                return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
            };

            const fileName = `factures${startDate ? '_' + getFormattedDate(startDate) : ''}${endDate ? '_' + getFormattedDate(endDate) : ''}.xlsx`;

            // Convertir en buffer et créer le blob
            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            // Créer le lien de téléchargement
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erreur lors de l\'export Excel:', error);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedBills = () => {
        if (!sortConfig.key) return paidBills;

        return [...paidBills].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'account':
                    aValue = a.accountName;
                    bValue = b.accountName;
                    break;
                case 'project':
                    aValue = a.opportunityName;
                    bValue = b.opportunityName;
                    break;
                case 'date':
                    // Convertir les TimeStamps en dates pour la comparaison
                    aValue = new Date(
                        parseInt(a.billDate.year),
                        parseInt(a.billDate.month) - 1,
                        parseInt(a.billDate.day)
                    );
                    bValue = new Date(
                        parseInt(b.billDate.year),
                        parseInt(b.billDate.month) - 1,
                        parseInt(b.billDate.day)
                    );
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return faSort;
        }
        return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
    };

    return (
        <div className="panel">
            <div className="panel-heading">
                <div className="panel-header-content">
                    <span className="icon-text">
                        <span className="icon">
                            <FontAwesomeIcon icon={faPercent} />
                        </span>
                        <span> TVA à déclarer</span>
                    </span>
                    <div className="panel-header-actions">
                        <input
                            type="date"
                            className="input is-small"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            placeholder="Date début"
                        />
                        <input
                            type="date"
                            className="input is-small"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            placeholder="Date fin"
                        />
                        <button
                            className="button is-small is-success"
                            onClick={handleExportExcel}
                            disabled={paidBills.length === 0}
                            title="Exporter en Excel"
                        >
                            <span className="icon is-small">
                                <FontAwesomeIcon icon={faFileExcel} />
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            <div className="panel-block">
                <div className="table-container">
                    <table className="table is-fullwidth is-striped is-hoverable mb-0">
                        <thead>
                            <tr>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('account')}
                                    >
                                        Compte <FontAwesomeIcon icon={getSortIcon('account')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('project')}
                                    >
                                        Projet <FontAwesomeIcon icon={getSortIcon('project')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('date')}
                                    >
                                        Date <FontAwesomeIcon icon={getSortIcon('date')} className="ml-1" />
                                    </button>
                                </th>
                                <th className="has-text-right">Montant HT</th>
                                <th className="has-text-right">TVA</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedBills().map((bill, index) => (
                                <tr key={index}>
                                    <td>{bill.accountName}</td>
                                    <td>{bill.opportunityName}</td>
                                    <td>{formatDate(bill.billDate)}</td>
                                    <td className="has-text-right">
                                        {bill.amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                    </td>
                                    <td className="has-text-right">
                                        {bill.vat.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                    </td>
                                </tr>
                            ))}
                            {paidBills.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="has-text-centered">Aucune facture à déclarer</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="panel-footer"></div>
        </div>
    );
};

export default BillsToDeclare;
